/**
 * LesserPlastic — main.js
 * Compiled by Gulp → dist/js/main.js
 */

document.addEventListener('DOMContentLoaded', () => {

  // ── Mobile nav toggle ────────────────────────────────────────────────────────
  const navToggle  = document.querySelector('.nav-toggle');
  const mobileNav  = document.getElementById('mobile-nav');

  if (navToggle && mobileNav) {
    navToggle.addEventListener('click', () => {
      const isOpen = navToggle.getAttribute('aria-expanded') === 'true';
      navToggle.setAttribute('aria-expanded', String(!isOpen));
      mobileNav.setAttribute('aria-hidden', String(isOpen));
      document.body.style.overflow = isOpen ? '' : 'hidden';
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!navToggle.contains(e.target) && !mobileNav.contains(e.target)) {
        navToggle.setAttribute('aria-expanded', 'false');
        mobileNav.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        navToggle.setAttribute('aria-expanded', 'false');
        mobileNav.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        navToggle.focus();
      }
    });
  }

  // ── Sticky header shadow on scroll ──────────────────────────────────────────
  const header = document.querySelector('.site-header');

  if (header) {
    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 10);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ── Auto Table of Contents ───────────────────────────────────────────────────
  const articleContent = document.getElementById('article-content');
  const tocNav         = document.querySelector('.toc-nav');

  if (articleContent && tocNav) {
    const headings = articleContent.querySelectorAll('h2, h3');

    if (headings.length > 2) {
      headings.forEach((heading, i) => {
        // Ensure each heading has an ID for anchor linking
        if (!heading.id) {
          heading.id = 'section-' + i + '-' + heading.textContent
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
        }

        const link = document.createElement('a');
        link.href = '#' + heading.id;
        link.textContent = heading.textContent;
        link.className = heading.tagName === 'H3' ? 'toc-h3' : 'toc-h2';
        tocNav.appendChild(link);
      });

      // Highlight active TOC item on scroll
      const tocLinks = tocNav.querySelectorAll('a');

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            tocLinks.forEach(link => link.classList.remove('is-active'));
            const active = tocNav.querySelector(`a[href="#${entry.target.id}"]`);
            if (active) active.classList.add('is-active');
          }
        });
      }, {
        rootMargin: '-20% 0px -70% 0px',
      });

      headings.forEach(h => observer.observe(h));
    } else {
      // Hide TOC if not enough headings
      const tocEl = document.getElementById('toc');
      if (tocEl) tocEl.style.display = 'none';
    }
  }

  // ── Smooth scroll for anchor links ──────────────────────────────────────────
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', (e) => {
      const target = document.querySelector(anchor.getAttribute('href'));
      if (target) {
        e.preventDefault();
        const headerHeight = header ? header.offsetHeight : 0;
        const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
        window.scrollTo({ top, behavior: 'smooth' });

        // Close mobile nav if open
        if (navToggle) {
          navToggle.setAttribute('aria-expanded', 'false');
          mobileNav?.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
        }
      }
    });
  });

  // ── Lazy image observer (enhances native lazy loading) ───────────────────────
  if ('IntersectionObserver' in window) {
    const lazyImages = document.querySelectorAll('img[loading="lazy"]');

    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-loaded');
          imageObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '200px 0px' });

    lazyImages.forEach(img => imageObserver.observe(img));
  }

  // ── Hero card stagger animation ──────────────────────────────────────────────
  const heroCards = document.querySelectorAll('.hero__visual-card');
  heroCards.forEach((card, i) => {
    card.style.opacity = '0';
    card.style.transform = 'translateY(20px)';
    card.style.transition = `opacity 0.5s ease ${0.2 + i * 0.15}s, transform 0.5s ease ${0.2 + i * 0.15}s`;

    // Trigger after a tick so transition fires
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        card.style.opacity = '1';
        card.style.transform = 'translateY(0)';
      });
    });
  });

  // ── Scroll reveal for sections ───────────────────────────────────────────────
  if ('IntersectionObserver' in window) {
    const revealEls = document.querySelectorAll(
      '.silo-card, .intro__pillar, .post-card, .intro__content, .intro__pillars'
    );

    revealEls.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(16px)';
      el.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
    });

    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          revealObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -60px 0px' });

    revealEls.forEach(el => revealObserver.observe(el));
  }

  // ── Copy link button ──────────────────────────────────────────────────────
  const copyBtn = document.querySelector('.article-share__btn--copy');
  if (copyBtn) {
    copyBtn.addEventListener('click', () => {
      const url = copyBtn.dataset.copyUrl || window.location.href;
      navigator.clipboard.writeText(url).then(() => {
        copyBtn.classList.add('is-copied');
        copyBtn.setAttribute('aria-label', 'Link copied!');
        setTimeout(() => {
          copyBtn.classList.remove('is-copied');
          copyBtn.setAttribute('aria-label', 'Copy link');
        }, 2000);
      });
    });
  }


  // ── Responsive table labels (mobile stacked view) ────────────────────────
  // Reads each column heading and adds it as data-label to every td
  // so the CSS ::before pseudo-element can display it as a label on mobile
  document.querySelectorAll('.prose table:not(.product-specs)').forEach(table => {
    const headers = [...table.querySelectorAll('thead th')].map(th => th.textContent.trim());
    if (!headers.length) return;
    table.querySelectorAll('tbody tr').forEach(row => {
      [...row.querySelectorAll('td')].forEach((td, i) => {
        if (headers[i]) td.setAttribute('data-label', headers[i]);
      });
    });
  });


});
